import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Checkout } from '@pdqdev/pdq-shared/dist/interfaces';
import { CurrencyUnit } from '@pdqdev/pdq-shared/dist/entities';
import { RuleEntity, RuleOperator } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { currencyUnitsToCurrencySymbolMap } from '@pdqdev/pdq-shared/dist/utils/currency-utils';
import { merge, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, map, switchMap, takeUntil } from 'rxjs/operators';
import { CheckoutService } from '@pages/checkout-app/checkout.service';
import { FormService } from '@pages/checkout-app/services/form/form.service';
import { DropdownOption } from '@storybook/components/dropdown/dropdown.component';
import {
  RulesEntitiesToDisplayNameMap,
  RulesEntityOperatorToDisplayNameMap,
} from '@app/sharedModule/components/rules-dynamic-form/model';

export const PERCENTAGE_LABEL = '% Percentage';

export const feeTypeTriggerFor =
  (symbol: () => string) =>
  (label: string): string =>
    label === PERCENTAGE_LABEL || label === '%' ? '%' : symbol();

// An array literal widens an enum member to the whole enum, which the narrow matcher union refuses.
type OperatorOption = { value: Checkout.GroupMatchOperator; label: string };

@Component({
  selector: 'item-fee-rules',
  templateUrl: './item-fee-rules.component.html',
  styleUrls: ['./item-fee-rules.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemFeeRulesComponent implements OnInit, OnDestroy {
  readonly PERCENT_GROUPING_HINT =
    'Grouping and partial-group handling apply to flat fees only. Percentage rules charge each matching item individually.';

  private readonly stepResetRules = new Set<AbstractControl>();

  @Input() formGroup: UntypedFormGroup;

  groupRules: UntypedFormArray;

  readonly matchByOptions = Checkout.GROUP_MATCH_ENTITIES.map((value) => ({
    value,
    label: RulesEntitiesToDisplayNameMap[value],
  }));

  readonly stepUnitOptions = [
    { value: Checkout.FeeUnitType.ITEM, label: 'items' },
    { value: Checkout.FeeUnitType.POUNDS, label: 'lb' },
  ];

  readonly roundingOptions = [
    { value: Checkout.GroupRounding.UP, label: 'Round up on partial' },
    { value: Checkout.GroupRounding.DOWN, label: 'Charge whole groups only' },
  ];

  feeTypeOptions = ItemFeeRulesComponent.buildFeeTypeOptions('USD');

  currencySymbol = 'USD';

  readonly feeTypeTrigger = feeTypeTriggerFor(() => this.currencySymbol);

  private readonly expandedRules = new Set<AbstractControl>();

  private destroy$ = new Subject<boolean>();

  private static readonly AUTOCOMPLETE_BY: Record<string, string> = {
    [RuleEntity.COLLECTION]: 'collection',
    [RuleEntity.PRODUCT_TYPE]: 'productType',
    [RuleEntity.VENDOR]: 'vendor',
    [RuleEntity.PRODUCT_TAG]: 'shopify_tags',
    [RuleEntity.SKU]: RuleEntity.SKU,
  };

  // Keyed by match-by AND term: rows share the unfiltered list but never each other's searches.
  private valueOptions: Record<string, DropdownOption[]> = {};
  private valueOptionsFailed: Record<string, boolean> = {};

  private readonly valueSearch$ = new Subject<{ matchBy: Checkout.GroupMatchBy; term: string }>();

  constructor(
    private checkoutService: CheckoutService,
    private formService: FormService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.groupRules = this.formGroup.get('feePerItem.groupRules') as UntypedFormArray;

    for (const matchBy of new Set((this.groupRules?.value ?? []).map((rule) => rule.matchBy))) {
      this.loadValueOptions(matchBy as Checkout.GroupMatchBy);
    }

    this.valueSearch$
      .pipe(
        debounceTime(300),
        switchMap(({ matchBy, term }) => this.fetchValueOptions(matchBy, term)),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => this.applyValueOptions(result));

    // markAllAsTouched (what the save gate calls) emits on `events` only, so without it the rows
    // that are blocking the save never turn red.
    merge(this.groupRules.valueChanges, this.groupRules.statusChanges, this.groupRules.events)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.pruneRowState();
        this.changeDetector.markForCheck();
      });

    this.checkoutService.shippingMethodCurrency$.pipe(takeUntil(this.destroy$)).subscribe((currency) => {
      const symbol = currencyUnitsToCurrencySymbolMap[currency as CurrencyUnit] as string;
      this.currencySymbol = symbol;
      this.feeTypeOptions = ItemFeeRulesComponent.buildFeeTypeOptions(symbol);
      this.changeDetector.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private static buildFeeTypeOptions(symbol: string): DropdownOption[] {
    return [
      {
        value: Checkout.GroupFeeType.PERCENT_OF_ITEM_PRICE,
        label: PERCENTAGE_LABEL,
        helpText: "Of each item's price",
      },
      { value: Checkout.GroupFeeType.FIXED, label: `${symbol} Fixed amount`, helpText: 'Charged per item' },
    ];
  }

  // An operator the match-by does not offer renders as a blank picker while still being priced.
  static allowedOperator(
    matchBy: Checkout.GroupMatchBy,
    operator: Checkout.GroupMatchOperator,
  ): Checkout.GroupMatchOperator {
    return ItemFeeRulesComponent.operatorsFor(matchBy).includes(operator) ? operator : RuleOperator.IS;
  }

  static createGroupRuleControl(rule: Partial<Checkout.FeePerItemGroupRule> = {}): UntypedFormGroup {
    const matchBy = rule.matchBy ?? RuleEntity.COLLECTION;
    return new UntypedFormGroup({
      matchBy: new UntypedFormControl(matchBy),
      operator: new UntypedFormControl(
        ItemFeeRulesComponent.allowedOperator(matchBy, rule.operator ?? RuleOperator.IS),
      ),
      value: new UntypedFormControl(rule.value ?? ''),
      amount: new UntypedFormControl(rule.amount ?? 0),
      type: new UntypedFormControl(rule.type ?? Checkout.GroupFeeType.FIXED),
      step: new UntypedFormControl(rule.step ?? 1),
      stepUnit: new UntypedFormControl(rule.stepUnit ?? Checkout.FeeUnitType.ITEM),
      firstNFree: new UntypedFormControl(rule.firstNFree ?? 0),
      rounding: new UntypedFormControl(rule.rounding ?? Checkout.GroupRounding.UP),
    });
  }

  static operatorsFor(matchBy: Checkout.GroupMatchBy): Checkout.GroupMatchOperator[] {
    return ItemFeeRulesComponent.OPERATOR_OPTIONS.filter(
      (option) => matchBy === RuleEntity.PRODUCT_TAG || !ItemFeeRulesComponent.TAG_ONLY.includes(option.value),
    ).map((option) => option.value);
  }

  private static readonly TAG_ONLY: Checkout.GroupMatchOperator[] = [RuleOperator.CONTAINS, RuleOperator.NOT_CONTAINS];

  private static readonly OPERATOR_OPTIONS: OperatorOption[] = Checkout.GROUP_MATCH_OPERATORS.map((value) => ({
    value,
    label: RulesEntityOperatorToDisplayNameMap[value],
  }));

  operatorOptionsFor(matchBy: Checkout.GroupMatchBy): OperatorOption[] {
    const allowed = ItemFeeRulesComponent.operatorsFor(matchBy);
    return ItemFeeRulesComponent.OPERATOR_OPTIONS.filter((option) => allowed.includes(option.value));
  }

  // Rules parked for the other mode are not priced, so they must not show as errors either.
  get isActive(): boolean {
    return (
      !!this.formGroup.get('feePerItem.enabled')?.value && !!this.formGroup.get('feePerItem.byProductGroup')?.value
    );
  }

  isValueMissing(rule: AbstractControl): boolean {
    return this.isActive && !`${rule.get('value').value ?? ''}`.trim() && rule.get('value').touched;
  }

  isPercentRule(rule: AbstractControl): boolean {
    return rule.get('type').value === Checkout.GroupFeeType.PERCENT_OF_ITEM_PRICE;
  }

  isRoundingRelevant(rule: AbstractControl): boolean {
    if (this.isPercentRule(rule)) {
      return false;
    }
    return Number(rule.get('step').value) > 1 || rule.get('stepUnit').value === Checkout.FeeUnitType.POUNDS;
  }

  stepLabel(rule: AbstractControl): string {
    const step = Number(rule.get('step').value) || 1;
    if (rule.get('stepUnit').value === Checkout.FeeUnitType.POUNDS) {
      return `${step} lb`;
    }
    return `${step} ${step === 1 ? 'item' : 'items'}`;
  }

  addRule(): void {
    const rule = ItemFeeRulesComponent.createGroupRuleControl();
    this.groupRules.push(rule);
    this.loadValueOptions(rule.get('matchBy').value);
    this.groupRules.markAsDirty();
  }

  deleteRule(index: number): void {
    const rule = this.groupRules.at(index);
    this.expandedRules.delete(rule);
    this.stepResetRules.delete(rule);
    this.groupRules.removeAt(index);
    this.groupRules.markAsDirty();
  }

  setValue(rule: AbstractControl, key: string, value: unknown): void {
    rule.get(key).setValue(value);

    if (key === 'type') {
      const switchedToPercent = value === Checkout.GroupFeeType.PERCENT_OF_ITEM_PRICE;
      if (switchedToPercent && Number(rule.get('step').value) > 1) {
        rule.get('step').setValue(1);
        this.stepResetRules.add(rule);
      } else if (!switchedToPercent) {
        this.stepResetRules.delete(rule);
      }
    }

    // Operators are per match-by; switching away from tags can strand `contains`.
    if (key === 'matchBy') {
      rule.get('value').setValue('');
      this.loadValueOptions(value as Checkout.GroupMatchBy);
      const matchBy = value as Checkout.GroupMatchBy;
      rule.get('operator').setValue(ItemFeeRulesComponent.allowedOperator(matchBy, rule.get('operator').value));
    }
    this.groupRules.markAsDirty();
  }

  private static cacheKey(matchBy: Checkout.GroupMatchBy, term: string): string {
    return `${matchBy}|${(term ?? '').trim().toLowerCase()}`;
  }

  valueOptionsFor(rule: AbstractControl): DropdownOption[] {
    return this.valueOptions[this.keyFor(rule)] ?? [];
  }

  valueOptionsUnavailable(rule: AbstractControl): boolean {
    return !!this.valueOptionsFailed[this.keyFor(rule)];
  }

  private keyFor(rule: AbstractControl): string {
    return ItemFeeRulesComponent.cacheKey(rule.get('matchBy').value, rule.get('value').value);
  }

  onValueSearch(matchBy: Checkout.GroupMatchBy, term: string): void {
    this.valueSearch$.next({ matchBy, term: term ?? '' });
  }

  private loadValueOptions(matchBy: Checkout.GroupMatchBy): void {
    const key = ItemFeeRulesComponent.cacheKey(matchBy, '');
    if (this.valueOptions[key] !== undefined) {
      return;
    }
    this.fetchValueOptions(matchBy, '')
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => this.applyValueOptions(result));
  }

  // Rows removed or replaced leave their control behind in these sets otherwise.
  private pruneRowState(): void {
    const live = new Set(this.groupRules?.controls ?? []);
    for (const set of [this.expandedRules, this.stepResetRules]) {
      for (const rule of [...set]) {
        if (!live.has(rule)) {
          set.delete(rule);
        }
      }
    }
  }

  // A failed lookup is not an empty one: without this the row reads "No results found" during an outage.
  private fetchValueOptions(
    matchBy: Checkout.GroupMatchBy,
    term: string,
  ): Observable<{ key: string; values: unknown[] | null } | null> {
    const autocompleteBy = ItemFeeRulesComponent.AUTOCOMPLETE_BY[matchBy];
    if (!autocompleteBy) {
      return of(null);
    }
    const key = ItemFeeRulesComponent.cacheKey(matchBy, term);
    return this.formService.conditionsAutocomplete(term, autocompleteBy).pipe(
      map((values) => ({ key, values: (values ?? []) as unknown[] })),
      catchError(() => of({ key, values: null })),
    );
  }

  private applyValueOptions(result: { key: string; values: unknown[] | null } | null): void {
    if (!result) {
      return;
    }
    this.valueOptionsFailed[result.key] = result.values === null;
    if (result.values) {
      this.valueOptions[result.key] = result.values.map((value) => ({
        label: String(value),
        value: String(value),
      }));
    }
    this.changeDetector.markForCheck();
  }

  toggleAdvanced(rule: AbstractControl): void {
    if (this.expandedRules.has(rule)) {
      this.expandedRules.delete(rule);
    } else {
      this.expandedRules.add(rule);
    }
  }

  isExpanded(rule: AbstractControl): boolean {
    return this.expandedRules.has(rule);
  }

  wasStepReset(rule: AbstractControl): boolean {
    return this.stepResetRules.has(rule);
  }

  moveRule(from: number, to: number): void {
    if (to < 0 || to >= this.groupRules.length) {
      return;
    }
    const controls = this.groupRules.controls;
    moveItemInArray(controls, from, to);
    this.groupRules.updateValueAndValidity();
    this.groupRules.markAsDirty();
  }

  onDrop(event: CdkDragDrop<unknown[]>): void {
    this.moveRule(event.previousIndex, event.currentIndex);
  }
}
